import React, { useEffect, useRef, useState } from 'react';

import cn from '@/lib/clsxm';
import { eventTracker } from '@/lib/service-workers';
import { useClientSmallScreen } from '@/lib/user-agent/client';

import Container from '@/components/@base/container';
import { Heading } from '@/components/@base/typography';
import { HEADING_TYPE } from '@/components/@base/typography/heading/type';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import XImage from '@/components/@base/x-image';
import DeveloperIntroduction from '@/components/app/product/components/title/developer-introduction';
import Props from '@/components/app/product/components/title/type';
import { useGetProfile } from '@/components/layout/services/hooks';
import { SequentialIcons } from '@/components/shared/components/icon';
import IconTextCombo from '@/components/shared/components/icon-text-combo';

import { useTourStore } from '@/store/tour.store';

import {
  DATA_CY_DEVELOPER_INTRODUCTION_LOGO,
  DATA_CY_DEVELOPER_INTRODUCTION_LOGO_ANIMATION,
  DATA_CY_DEVELOPER_INTRODUCTION_LOGO_BORDER,
  DATA_CY_DEVELOPER_NAME,
  DATA_CY_MEDAL_ICON,
} from '@/constant/cypress';
import imagesList from '@/constant/images-list';
import localization from '@/constant/localization';

export default function Title({ title, store, medals, ...props }: Props) {
  const { profile } = useGetProfile();
  const isSmallScreen = useClientSmallScreen();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [shouldRenderPopup, setShouldRenderPopup] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const popupWrapperRef = useRef<HTMLDivElement | null>(null);
  const popupContentRef = useRef<HTMLDivElement | null>(null);
  const [showRing, setShowRing] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [isClickTriggered, setIsClickTriggered] = useState(false);
  const { showTour } = useTourStore();

  const merchantEventHandler = () => {
    eventTracker(profile?.mobile, 'merchant_click', new Date().toISOString(), {
      vendor_name: store?.name,
      vendor_id: store?.id,
    });
  };
  const handleVendorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    calculatePopupPosition();
    setIsPopupOpen((prev) => !prev);
    merchantEventHandler();
  };
  const calculatePopupPosition = () => {
    if (popupWrapperRef.current) {
      const rect = popupWrapperRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 8,
        left: rect.right + window.scrollX - (isSmallScreen ? 345 : 350),
      });
    }
  };

  useEffect(() => {
    setIsClient(true);
    setShowRing(true);
  }, []);

  useEffect(() => {
    if (!isSmallScreen || !isPopupOpen) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (popupWrapperRef.current && !popupWrapperRef.current.contains(e.target as Node)) {
        setIsPopupOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isSmallScreen, isPopupOpen]);

  useEffect(() => {
    if (isPopupOpen) {
      setShouldRenderPopup(true);
      const showTimer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsPopupVisible(true);
        });
      });
      const handleScroll = () => {
        if (popupWrapperRef.current) {
          calculatePopupPosition();
        }
      };
      const handleResize = () => {
        if (popupWrapperRef.current) {
          calculatePopupPosition();
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize);

      return () => {
        cancelAnimationFrame(showTimer);
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    } else {
      setIsPopupVisible(false);
    }
  }, [isPopupOpen, isSmallScreen]);

  useEffect(() => {
    if (!shouldRenderPopup || isPopupVisible || !popupContentRef.current) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      const target = e.target as HTMLElement;
      if (
        e.propertyName === 'opacity' &&
        !isPopupVisible &&
        popupContentRef.current &&
        (popupContentRef.current.contains(target) || popupContentRef.current === target)
      ) {
        setShouldRenderPopup(false);
      }
    };

    const popupElement = popupContentRef.current;
    popupElement.addEventListener('transitionend', handleTransitionEnd, true);

    return () => {
      popupElement.removeEventListener('transitionend', handleTransitionEnd, true);
    };
  }, [shouldRenderPopup, isPopupVisible]);

  useEffect(() => {
    if (showTour) {
      setIsPopupOpen(false);
      setIsPopupVisible(false);
      setShouldRenderPopup(false);
    }
  }, [showTour]);

  return (
    <Container className='mx-auto px-4 md:m-0 md:border-b-[1px] md:border-b-[#F5F6F9] md:p-[26px]'>
      <Heading
        {...props}
        data-nosnippet
        className='text-[22px] font-bold text-[#424244] md:pb-4'
        type={HEADING_TYPE.H1}
      >
        {title}
      </Heading>
      <Container className='relative mt-5 mb-6 flex w-full items-center justify-between overflow-visible md:m-0'>
        <Container className='relative mr-0'>
          <div className='inline-flex'>
            <IconTextCombo
              Icon={
                <div
                  ref={popupWrapperRef}
                  onClick={handleVendorClick}
                  onMouseEnter={() => {
                    if (!isSmallScreen) {
                      calculatePopupPosition();
                      setIsPopupOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!isSmallScreen && !isClickTriggered) {
                      setIsPopupOpen(false);
                    }
                  }}
                  className={cn(
                    store.slug !== 'zhaket' && 'cursor-pointer',
                    'relative flex items-center justify-center rounded-full',
                  )}
                  data-cy={DATA_CY_DEVELOPER_INTRODUCTION_LOGO}
                >
                  <XImage
                    src={store?.logo?.publicLink || imagesList.images.productDefaultCover}
                    alt={store?.name}
                    width={30}
                    height={30}
                    className='relative z-10 rounded-full border-2 border-[#FFFFFF] shadow-[0px_2px_8px_0px_#0000000D]'
                  />
                  <div className='absolute inset-0 flex items-center justify-center'>
                    {showRing && store.slug !== 'zhaket' && (
                      <>
                        <div
                          data-cy={DATA_CY_DEVELOPER_INTRODUCTION_LOGO_ANIMATION}
                          className='border-gradient-spin'
                        >
                          <svg viewBox='0 0 120 120'>
                            <circle cx='60' cy='60' r='57' />
                          </svg>
                        </div>
                        <div
                          data-cy={DATA_CY_DEVELOPER_INTRODUCTION_LOGO_BORDER}
                          className='border-static'
                        />
                      </>
                    )}
                  </div>
                </div>
              }
              textType={TEXT_TYPE.P}
              textClassName={cn(
                store.slug !== 'zhaket' && 'cursor-pointer',
                'font-medium text-[#878F9B] truncate',
              )}
              title={`${localization.vendor} : ${store?.name}`}
              onMouseEnter={() => {
                if (!isSmallScreen) {
                  calculatePopupPosition();
                  setIsPopupOpen(true);
                }
              }}
              onMouseLeave={() => {
                if (!isSmallScreen && !isClickTriggered) {
                  setIsPopupOpen(false);
                }
              }}
              onClick={handleVendorClick}
              data-cy={DATA_CY_DEVELOPER_NAME}
            />
          </div>
          {isClient && store.slug !== 'zhaket' && shouldRenderPopup && (
            <div
              className="pointer-events-none fixed z-[50] before:absolute before:-top-3 before:right-5 before:z-[50] before:border-8 before:border-transparent before:border-b-white before:content-['']"
              style={{
                top: `${popupPosition.top}px`,
                left: `${popupPosition.left}px`,
                transform: 'translateX(0)',
              }}
            >
              <div ref={popupContentRef} className='pointer-events-auto'>
                <DeveloperIntroduction store={store} isVisible={isPopupVisible} />
              </div>
            </div>
          )}
        </Container>

        {medals && (
          <Container className='w-full max-w-[168px]'>
            <SequentialIcons
              data-cy={DATA_CY_MEDAL_ICON}
              icons={medals}
              defaultIconUrl={imagesList.icons.defaultMedalIcon}
              size={36}
              iconClassName='absolute h-10 w-10 rounded-full border-none'
              wrapperClassName='relative h-10'
              shiftAmount={25}
            />
          </Container>
        )}
      </Container>
    </Container>
  );
}
